import {
  ArrowDownTrayIcon, ShareIcon, XMarkIcon, CheckCircleIcon, StarIcon,
} from '@heroicons/react/24/solid';
import { ChatBubbleLeftEllipsisIcon, PhoneIcon } from '@heroicons/react/20/solid';

import { TailwindSpinner } from '@components/common/Elements/Spinner';
import {
  MouseEvent, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  CvDetailAvailableOperations,
  CvDetailAvailableOperationsCurrentStatus,
  CvDetailObject,
} from '@components/templates/cv/interface/CvDetailInterface';
import {
  Button, Modal, Tooltip,
  Dropdown, DropdownButton, DropdownItems, DropdownItem,
} from '@components/common';
import { useBjModal } from '@hooks/common/useBjModal';
import { ShareModal } from '@components/features/modals/share-modal/ShareModal';
import { CustomConfirmModalContent } from '@components/templates/cv/partials/modals/CustomConfirmModalContent';
import { useTranslation } from 'next-i18next';
import { useStaticToasts } from '@hooks/useStaticToasts/useStaticToasts';
import { SendMessageModalContent } from '@components/templates/cv/partials/modals/SendMessageModalContent';
import { ResumeArrowsNavigation } from '@components/templates/cv/partials/navigation/ResumeArrowsNavigation';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import {
  ApiCheckUnlock,
  fetcher,
  isApiV1Errors,
  isCheckUnlockStatusOk,
  ResponseWithHeaders,
} from '@utils/data-fetching';
import useWindowDimensions from '@hooks/useMediaQuery';
import { useAppDispatch, useAppSelector } from 'src/app';
import { AddressBookSolidIcon } from '@components/common/Icons';
import { useApplicantListsRedux } from '@components/templates/employer/cards-lists/hooks/useApplicantListsRedux';
import { useOpenTokCallStart } from '@components/features/openTokCall';
import { ProfileOperationItemButton } from '@components/templates/cv/sections/ProfileOperationItemButton';
import { useSwrAvailableResumeOperations } from '@hooks/useSwrApi/endpoints/user';
import { useSwrInterviewRequest, useSwrGenerateInterviewQuestions, useSwrSaveInterviewQuestions } from '@hooks/useSwrApi/endpoints/interview';
import { filterAndOrderAvailableOperations } from '@components/templates/cv/utils';
import { InterviewQuestionsModal } from '@components/features/modals/interview-questions-modal';
import { getCommentUnlockResponse } from '@store/reducers/employer/resume/resumeSelector';
import { clearCommentUnlockResponse } from '@store/reducers/employer/resume/resumeReducer';
import { EmployerResumeUnlockResponse } from '@type/redux';
import { getHoverClassesByOperation } from '@components/templates/cv/utils/operations';
import { useSwrResumeCheckUnlockPrice } from '@hooks/useSwrApi/endpoints/resume-operations/useSwrResumeCheckUnlockPrice';
import { showAddCompanyInfoModal } from '@components/features/modals/employer/AddCompanyInfoModal';
import { trackEvent } from '@services/tracking';
import { useAuthClient } from '@root/src/hooks';
import { useUnblockOperations } from '@components/templates/cv/hooks/useUnblockOperations';
import { InterfaceJobFolderOptions } from '@components/templates/cv/sections/ProfileHeading';
import { LOCAL_STORAGE_KEYS } from '@utils/local-storage/localStorage';

export const UnblockActions = ({
  cvDetail,
  reFetchData,
  jobFolderId,
  isSinglePage,
  jobTitle,
  jobSlug,
  sendCurrentCvStatus,
  slug,
  setLoadingCvStatus,
  setTelephoneHighlight,
  setCurrentJobFolderHighlight,
  isFromJobContext,
  applicantSeenContact,
  applicantSeenContactStatus,
  closeCvModal,
  isCompany,
  rid,
  withNavbarInModal,
  locale,
  statusChangeFromModal,
  setJobFolderInfo,
  hasSourcingPackage = false,
}: { cvDetail:CvDetailObject;
  setLoadingCvStatus:()=>void;
  setJobFolderInfo: (jobFolderInfo: InterfaceJobFolderOptions) => void;
  statusChangeFromModal?:(statusId: string, slug:string)=>void;
  setTelephoneHighlight:()=>void;
  setCurrentJobFolderHighlight:()=>void;
  jobSlug:string;
  jobTitle:string;
  isSinglePage?:boolean;
  slug:string;
  jobFolderId:number;
  isFromJobContext:boolean;
  applicantSeenContactStatus:boolean;
  reFetchData:(onSuccessCallback?: (cv: CvDetailObject) => void) => void;
  applicantSeenContact:() => void;
  sendCurrentCvStatus:(item: CvDetailAvailableOperationsCurrentStatus) => void;
  closeCvModal?: () => void;
  isCompany: boolean;
  rid?: string;
  withNavbarInModal?: boolean;
  locale: Locale;
  hasSourcingPackage?: boolean;
}) => {
  const { t } = useTranslation('common');
  const userSlug = cvDetail.slug;
  const userIdToUnlock = cvDetail?.id as number;
  const [sendMessageModal, setSendMessageModal] = useState<boolean>(false);
  const [unlockedByCall, setUnlockedByCall] = useState<boolean>(false);
  const [currentCVStatus, setCurrentCVStatus] = useState<CvDetailAvailableOperationsCurrentStatus>();
  const [currentJobSlug, setCurrentJobSlug] = useState<string>('');
  const [interviewJobId, setInterviewJobId] = useState<number>(cvDetail?.application?.jobId || jobFolderId || 0);
  const [pendingInterviewRequest, setPendingInterviewRequest] = useState<boolean>(false);
  const telRef = useRef<HTMLAnchorElement>(null);
  const { status200Toast, showStatusToast } = useStaticToasts();
  const { isMobile } = useWindowDimensions();
  const headerInfo = useAppSelector((state) => state.user.headerInfo);
  const userId = headerInfo?.userId;
  const [isLoadingStatusChange, setIsLoadingStatusChange] = useState<boolean>(false);
  const [interviewRequestSent, setInterviewRequestSent] = useState<boolean>(false);
  const [showValidateHintModal, setShowValidateHintModal] = useState<boolean>(false);
  const [isValidateFlowLoading, setIsValidateFlowLoading] = useState<boolean>(false);

  const {
    requestUnlockWithConfirm,
    confirmModalOpen,
    confirmUnlock,
    cancelUnlock,
  } = useUnblockOperations({
    rid,
    userIdToUnlock,
    isCompany,
    jobSlug,
    jobTitle,
    jobFolderId,
    locale,
    userId,
    slug,
    ...(jobSlug && { jobSlug }),
  });

  // Interview request mutation
  const { trigger: triggerInterviewRequest, isMutating: isInterviewRequestLoading } = useSwrInterviewRequest({
    locale,
    jobSlug,
    userSlug,
    onSuccess: () => {
      setInterviewRequestSent(true);
      setIsValidateFlowLoading(false);
      status200Toast(t('cv.detail.action.interview_request_sent'));
      reFetchData();
    },
  });

  // Interview questions state and modal
  const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState<boolean>(false);
  const [generatedQuestions, setGeneratedQuestions] = useState<string[]>([]);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState<boolean>(false);

  // Generate interview questions mutation
  const { trigger: triggerGenerateQuestions } = useSwrGenerateInterviewQuestions({
    locale,
    jobId: interviewJobId,
    onSuccess: (response) => {
      setGeneratedQuestions(response.questions);
      setIsQuestionsModalOpen(true);
      setIsGeneratingQuestions(false);
      setIsValidateFlowLoading(false);
    },
    onError: () => {
      setIsGeneratingQuestions(false);
      setIsValidateFlowLoading(false);
    },
  });

  // Save interview questions mutation
  const { trigger: triggerSaveQuestions, isMutating: isSavingQuestions } = useSwrSaveInterviewQuestions({
    locale,
    jobSlug,
    onSuccess: () => {
      setIsQuestionsModalOpen(false);
      // After successfully saving questions, proceed with the interview request
      void triggerInterviewRequest();
    },
    onError: () => {
      // Error toast is already shown by the hook
    },
  });

  // Redux states for checking external operations response
  const commentUnlockResponse = useAppSelector(getCommentUnlockResponse);
  const dispatch = useAppDispatch();


  const { isAuth } = useAuthClient();

  // Redux states for infinite lists. When in modal, with this we watch if:
  // - The applicant/talent status has changed (interview, shortlist, etc.).
  // - The CV was previously locked but now has been unlocked via message, show contact details, download, etc.
  // We will mutate the infinite list according to these states.
  // *************************************
  const [statusChanged, setStatusChanged] = useState<boolean>(false);
  const [lockedChanged, setLockedChanged] = useState<boolean>(false);
  const [isFreeCvInfoUnblock, setIsFreeCvInfoUnblock] = useState<boolean>(false);
  const [newStatusId, setNewStatusId] = useState<number | undefined>();
  useApplicantListsRedux({
    statusChanged, lockedChanged, slug, statusId: newStatusId,
  });


  // SWR mutation: Unlock for Message
  // ******************************************************
  const {
    trigger: triggerCheckUnlockPrice,
  } = useSwrResumeCheckUnlockPrice({
    userSlug,
    locale,
    successCallback: (response) => {
      // If the response is false, the contact cost is 10 neuro
      setIsFreeCvInfoUnblock(response.is_unlock);
    },
  });


  // Handle share modal
  // *************************************
  const { isOpen: isShareModalOpen, toggleModal: toggleShareModal } = useBjModal();


  // start video call options
  const { onShowVideoOptions } = useOpenTokCallStart();

  // SWR: Get available operations
  const { data: availableResumeOperations, mutate: refetchResumeOperations } = useSwrAvailableResumeOperations({
    locale,
    slug,
    shouldFetch: isAuth && isCompany,
    queryParams: {
      jobSlug: jobSlug?.length ? jobSlug : currentJobSlug,
      rid,
    },
    onSuccess: (response) => {
      setCurrentCVStatus(response?.currentStatus as CvDetailAvailableOperationsCurrentStatus);
      sendCurrentCvStatus(response?.currentStatus as CvDetailAvailableOperationsCurrentStatus);
    },
  });


  // Refetch available operations when conditions change (race condition fix)
  // This ensures data is fetched even if headerInfoData loads after component mount
  useEffect(() => {
    if (isAuth && isCompany && !availableResumeOperations) {
      void refetchResumeOperations();
    }
  }, [isAuth, isCompany, availableResumeOperations, refetchResumeOperations]);

  const downloadEndpoint = availableResumeOperations?.availableOperations.find((op) => op.operationId === 'download')?.operationEndpoint;


  // List of available operations filtered and ordered
  const availableOperations = useMemo(() => filterAndOrderAvailableOperations(
    availableResumeOperations?.availableOperations ?? [],
  ), [availableResumeOperations?.availableOperations]);

  const handleSuccessStatusChanged = useCallback((response: EmployerResumeUnlockResponse) => {
    setCurrentJobSlug(response.response.jobSlug);
    // If the response status is success we need to re-fetch the available operations
    void refetchResumeOperations();
    sendGoogleTagEvent(`employer_cv_set_${response.operationName?.toLowerCase()}`, 'Custom');
    setIsLoadingStatusChange(false);
    setStatusChanged(true);
    setNewStatusId(response.statusId);
  }, [refetchResumeOperations]);

  useEffect(() => {
    if (commentUnlockResponse) {
      dispatch(clearCommentUnlockResponse());
      handleSuccessStatusChanged(commentUnlockResponse);
    }
  }, [commentUnlockResponse, dispatch, handleSuccessStatusChanged]);


  useEffect(() => {
    void triggerCheckUnlockPrice({ jobId: jobFolderId, title: jobTitle, jobSlug });
  }, [triggerCheckUnlockPrice, jobFolderId, jobTitle, jobSlug]);

  useEffect(() => {
    if (!interviewJobId && cvDetail?.application?.jobId) {
      setInterviewJobId(cvDetail.application.jobId);
    } else if (!interviewJobId && jobFolderId) {
      setInterviewJobId(jobFolderId);
    }
  }, [interviewJobId, cvDetail?.application?.jobId, jobFolderId]);

  const sendContactedData = () => {
    fetcher(`/v1/resume-operation/${userIdToUnlock}/contact${rid ? `?rid=${rid}` : ''}`, locale, {
      method: 'POST',
      payload: {
        type: 'contact',
        jobSlug,
      },
      stringifyPayload: true,
    }).then((response) => {
      if (isApiV1Errors(response)) {
        const { code } = response.errors[0];
        showStatusToast(String(`${code}`));
      } else if (!applicantSeenContactStatus) {
        applicantSeenContact();
        setTelephoneHighlight();
      }
    }).catch(() => {
    });
  };


  const isPromoPrice = !!(cvDetail?.fullPrice && cvDetail?.price && cvDetail?.price < cvDetail?.fullPrice);

  // we use the fetcher version with headers, because we need to get the filename from the response headers
  const getDownloadedPDF = () => {
    const apiURL = rid ? `${downloadEndpoint}?rid=${rid}` : `${downloadEndpoint}`;
    fetcher<unknown>(apiURL, locale, {
      method: 'GET',
      responseType: 'blob',
      withResponseHeaders: true,
    }).then((res) => {
      // this is how you make the response headers available and let TS know about it (assert res as ResponseWithHeaders<T>)
      const { response, responseHeaders }: { response: BlobPart, responseHeaders: Headers } = res as ResponseWithHeaders<BlobPart>;
      const filename = responseHeaders.get('content-disposition')?.split('filename=')[1];
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `${userSlug}.pdf`);
      document.body.appendChild(link);
      link.click();
      reFetchData();
      //   aici punem tracking event de download
      trackEvent({
        eventType: 'action',
        entityType: 'resume',
        entityId: slug,
        action: 'contact',
        viewContext: 'detail',
        ...(userId && { userId: userId.toString() }),
        ...(rid && { listUuid: rid }),
      });
    }).catch(() => {});
  };

  // Unlock with PDF if the confirmation modal is true
  const unlockWithDownloadPdf = () => {
    try {
      requestUnlockWithConfirm({
        type: 'download',
        onSuccess: (res) => {
          if (isCheckUnlockStatusOk(res)) {
            sendGoogleTagEvent('premium_contact_download', 'Custom');
            getDownloadedPDF();
            reFetchData();
            void refetchResumeOperations();
            setLockedChanged(true);
            if (res.meta?.consumptionInfoSummary) {
              status200Toast(res.meta.consumptionInfoSummary);
            }
          }
        },
      });
    } catch (err) { console.warn('error'); }
  };


  // If the CV in unlocked than we can download the PDF else we are going in the process
  const checkUnlockPdf = () => {
    if (cvDetail.locked === false) {
      getDownloadedPDF();
    } else {
      unlockWithDownloadPdf();
    }
  };


  const unlockWithMessage = () => {
    if (cvDetail.locked === false) {
      setSendMessageModal(true);
    } else {
      try {
        requestUnlockWithConfirm({
          type: 'message',
          onSuccess: (res) => {
            if (isCheckUnlockStatusOk(res)) {
              setCurrentJobFolderHighlight();
              setSendMessageModal(true);
              reFetchData();
              void refetchResumeOperations();
              sendGoogleTagEvent('premium_contact_message', 'Custom');
              setLockedChanged(true);
              if (res.meta?.consumptionInfoSummary) {
                status200Toast(res.meta.consumptionInfoSummary);
              }
            }
          },
        });
      } catch (err) { console.warn('error'); }
    }
  };

  const unlockContactData = (onSuccessCallback?: (cv: CvDetailObject) => void) => {
    if (cvDetail?.privateInformation?.phone !== undefined && unlockedByCall) {
      setUnlockedByCall(false);
      if (onSuccessCallback) onSuccessCallback(cvDetail);
    } else {
      try {
        requestUnlockWithConfirm({
          type: 'contact',
          onSuccess: (res) => {
            if (isCheckUnlockStatusOk(res)) {
              const resolvedJobId = res.jobId || jobFolderId || cvDetail?.application?.jobId || 0;
              if (resolvedJobId && resolvedJobId !== interviewJobId) {
                setInterviewJobId(resolvedJobId);
              }
              sendContactedData();
              setCurrentJobFolderHighlight();
              setUnlockedByCall(true);
              reFetchData(onSuccessCallback);
              void refetchResumeOperations();
              sendGoogleTagEvent('premium_contact_call', 'Custom');
              setTimeout(() => {
                telRef?.current?.click();
              }, 750);
              setLockedChanged(true);
              if (res.meta?.consumptionInfoSummary) {
                status200Toast(res.meta?.consumptionInfoSummary);
              }
            }
          },
        });
      } catch (err) { console.warn('error'); }
    }
  };

  // Change status to specific operation, each item has the parameters in the available
  // action we need to get them out
  const applyStatusChange = useCallback((params:CvDetailAvailableOperations) => {
    setIsLoadingStatusChange(true);
    const eventName = (params?.name ?? '').toLowerCase();
    const uId = params.operationParams.filter((x:{ id:string }) => x.id === 'userId').map((item : { value:number }) => item.value)[0];
    const jobId = params.operationParams.filter((x:{ id:string }) => x.id === 'jobId').map((item : { value:number }) => item.value)[0];
    const statusId = params.operationParams.filter((x:{ id:string }) => x.id === 'status').map((item : { value:number }) => item.value)[0];

    // In case of CV view, if we have dropdown for job select we need to overwrite the job context,
    // so that the operation will be applied to job context
    const jobFinalId = jobId === undefined ? jobFolderId : jobId;

    fetcher<ApiCheckUnlock>(`${params.operationEndpoint}${rid ? `?rid=${rid}` : ''}`, locale, {
      method: 'POST',
      payload: {
        userId: uId,
        status: statusId,
        jobId: jobFinalId,
        title: jobTitle,
      },
      stringifyPayload: true,
    }).then((response) => {
      if (isCheckUnlockStatusOk(response)) {
        // aplicam tracking pentru status change
        if (statusId === 3) {
          trackEvent({
            eventType: 'action',
            entityType: 'resume',
            entityId: slug,
            action: 'interview',
            viewContext: 'detail',
            ...(userId && { userId: userId.toString() }),
            ...(rid && { listUuid: rid }),
          });
        }

        handleSuccessStatusChanged({
          response,
          operationName: eventName,
          statusId,
        });
        statusChangeFromModal?.(statusId.toString(), slug);
      } else if (isApiV1Errors(response)) {
        const { code } = response.errors[0];
        if (code === 403) {
          const successCallback = () => applyStatusChange(params);
          showAddCompanyInfoModal(locale, successCallback);
          setIsLoadingStatusChange(false);
          return;
        }
        showStatusToast(String(`${code}`));
        setIsLoadingStatusChange(false);
      }
    }).catch(() => {});
  }, [jobFolderId, rid, locale, jobTitle, showStatusToast, handleSuccessStatusChanged, statusChangeFromModal, slug, userId]);

  // Handle interview request button click
  const handleInterviewRequest = useCallback(() => {
    // Check if job has enabled questions
    if (cvDetail?.application?.enabledQuestions) {
      // If questions are already enabled, proceed directly with interview request
      void triggerInterviewRequest();
    } else {
      if (!interviewJobId) {
        setIsValidateFlowLoading(false);
        return;
      }
      // Generate interview questions first (job details fetched from DB using jobId in URL)
      setIsGeneratingQuestions(true);
      void triggerGenerateQuestions({});
    }
  }, [cvDetail?.application?.enabledQuestions, triggerInterviewRequest, triggerGenerateQuestions, interviewJobId]);

  useEffect(() => {
    if (pendingInterviewRequest && interviewJobId) {
      setPendingInterviewRequest(false);
      handleInterviewRequest();
    } else if (pendingInterviewRequest && !interviewJobId) {
      setPendingInterviewRequest(false);
    }
  }, [pendingInterviewRequest, interviewJobId, handleInterviewRequest]);

  // Handle saving interview questions
  const handleSaveQuestions = useCallback((questions: string[]) => {
    void triggerSaveQuestions({ questions });
  }, [triggerSaveQuestions]);

  // Handle closing the questions modal
  const handleCloseQuestionsModal = useCallback(() => {
    setIsQuestionsModalOpen(false);
    setGeneratedQuestions([]);
  }, []);

  // We need to check if the phone was unlocked by request, or it can make already call without
  // checking unlock
  useEffect(() => {
    if (cvDetail?.privateInformation?.phone !== undefined && unlockedByCall) {
      setUnlockedByCall(false);
    }
  }, [unlockedByCall, cvDetail]);

  const setLoadingCvStatusFromOperation = () => {
    setLoadingCvStatus();
  };

  const triggerPhoneLoad = (event: MouseEvent) => {
    if (!isMobile) {
      event.preventDefault();
    }
    setTelephoneHighlight();
  };

  // Helper component
  // *****************************************************************************************************
  // Return the user status
  // *****************************************************************************************************
  const returnUserStatus = useCallback(() => {
    const defaultClasses = 'text-pico lg:text-tiny uppercase text-ink flex items-center absolute shadow-xl bg-surface rounded-xl px-2 bottom-0 mt-2 text-nowrap';

    if (cvDetail?.online) {
      return (
        <span className={defaultClasses}>
          <span className="mr-2 block size-2.5 rounded-full bg-secondary" />
          {t('cv.detail.action.online.label')}
        </span>
      );
    }
    return null;
  }, [t, cvDetail?.online]);


  // Helper component
  // *****************************************************************************************************
  // Available operations buttons based on the current status and showing position (left right)
  // Left buttons are for the rejected status, right buttons are for the other statuses
  // *****************************************************************************************************
  const availableOperationsButtons = useCallback((isLeftButton: boolean) => {
    if (isLoadingStatusChange || !availableOperations?.length) {
      return null;
    }

    // Filter the operations based on the current status
    const filteredOperations = availableOperations.filter((item) => {
      const leftCondition = (item.operationId === 'status_2' && currentCVStatus?.id === '4') || item.operationId === 'status_4';
      if (isLeftButton) {
        return leftCondition;
      }
      return !leftCondition;
    });


    return (
      <>
        { filteredOperations.map((item) => {
          const hoverClasses = getHoverClassesByOperation(item);

          return (
            <div key={item.name}>
              <div>
                <div
                  role="presentation"
                  onClick={() => applyStatusChange(item)}
                  className={`mx-1 inline-flex
                            cursor-pointer items-center justify-center
                            rounded-full bg-surface px-4.5
                            py-3.5 text-md font-medium transition-all duration-200
                            hover:shadow-md disabled:opacity-60  ${hoverClasses}
                  ${item.operationId === 'status_21' ? ' bg-primary/10 text-primary' : ''}
                  ${item.operationId === 'status_22' ? ' bg-secondary/10 text-secondary' : ''}
                  ${item.operationId === 'status_23' ? ' bg-warning/20 text-warning' : ''}
                  ${item.operationId === 'status_3' ? ' bg-pink/10 text-pink' : ''}
                  ${item.operationId === 'status_4' ? ' bg-primary/10 text-primary' : ''}
                  ${(item.operationId === 'status_2' && currentCVStatus?.id === '22') ? ' !bg-secondary/10 text-secondary' : ''}
                  ${(item.operationId === 'status_2' && currentCVStatus?.id === '4') ? ' !bg-error/10 text-error' : ''}
              `}
                >
                  <ProfileOperationItemButton operationItem={item} currentCVStatus={currentCVStatus} />
                </div>
              </div>
            </div>
          );
        })}
      </>
    );
  }, [isLoadingStatusChange, availableOperations, currentCVStatus, applyStatusChange]);


  // success data unlocked - show the video call options
  const onSuccessContactDataUnlocked = (cv: CvDetailObject, withCall = false) => {
    if (withCall) {
      onShowVideoOptions({
        userId: cv.id as number,
        userName: cv.privateInformation?.name ?? '',
        slug: cv.slug,
        imgUrl: cv.imgUrl,
        email: cv.privateInformation?.email,
        phone: cv.privateInformation?.phone,
      });
    }
  };

  // Unlock contact data and show the phone number.
  // Show call options based on the `withCall` parameter
  const onViewContactDataPressed = async (event: MouseEvent | null, withCall = false, onSuccess?: (cv: CvDetailObject) => void) => {
    // Check if the user has a phone number, if not we need to unlock it
    if (cvDetail?.privateInformation?.phone === undefined && !unlockedByCall) {
      unlockContactData((cv) => {
        onSuccessContactDataUnlocked(cv, withCall);
        if (onSuccess) onSuccess(cv);
      });
      return;
    }
    if (!applicantSeenContactStatus) {
      sendContactedData();
      if (onSuccess) onSuccess(cvDetail);
      return;
    }
    if (event) {
      triggerPhoneLoad(event);
    }
    onSuccessContactDataUnlocked(cvDetail, withCall);
    if (onSuccess) onSuccess(cvDetail);
  };

  const handleValidateAction = useCallback((event: MouseEvent | null) => {
    const isCvLocked = isCompany && cvDetail?.id !== userId
      && !cvDetail?.privateInformation?.phone
      && !cvDetail?.privateInformation?.email;

    if (isCvLocked) {
      void onViewContactDataPressed(event, false, () => {
        setPendingInterviewRequest(true);
      });
      return;
    }

    handleInterviewRequest();
  }, [cvDetail?.id, cvDetail?.privateInformation?.phone, cvDetail?.privateInformation?.email, handleInterviewRequest, isCompany, onViewContactDataPressed, userId]);

  const handleCloseValidateHintModal = useCallback(() => {
    setShowValidateHintModal(false);
    setIsValidateFlowLoading(false);
  }, []);

  const handleContinueValidate = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(LOCAL_STORAGE_KEYS.shownValidateHintModal, 'true');
    }
    setShowValidateHintModal(false);
    handleValidateAction(null);
  }, [handleValidateAction]);

  const handleValidateClick = useCallback((event: MouseEvent) => {
    setIsValidateFlowLoading(true);
    const hasSeenValidateHint = typeof window !== 'undefined'
      && localStorage.getItem(LOCAL_STORAGE_KEYS.shownValidateHintModal) === 'true';

    if (!hasSeenValidateHint) {
      setShowValidateHintModal(true);
      return;
    }

    handleValidateAction(event);
  }, [handleValidateAction]);

  // Render main component
  // *****************************************************************************************************
  return (
    <>

      {/* Share modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={toggleShareModal}
        pathnameShareUrl={locale === 'ro' ? `/cv/${userSlug}` : `/${locale}/cv/${userSlug}`}
        ogTitle={t('cv.detail.meta.title')}
        ogDescription={`Bestjobs - ${t('cv.detail.meta.title')} - ${userSlug}`}
      />

      {isAuth && (
        <>
          <Modal
            isOpen={showValidateHintModal}
            onClose={handleCloseValidateHintModal}
            size="sm"
            centered
          >
            <Modal.Header onClose={handleCloseValidateHintModal}>
              <p className="text-lg text-ink">
                {t('validate.modal.title')}
              </p>
            </Modal.Header>
            <Modal.Body>
              <p className="text-sm text-ink">
                {t('validate.modal.description')}
              </p>
            </Modal.Body>
            <Modal.Footer className="flex justify-end">
              <Button color="primary" onClick={handleContinueValidate}>
                {t('validate.modal.cta')}
              </Button>
            </Modal.Footer>
          </Modal>
          <Modal
            size="sm"
            isOpen={confirmModalOpen}
            body={(
              <CustomConfirmModalContent
                jobContextDetail={{
                  jobFolderId,
                  title: jobTitle,
                  jobSlug,
                }}
                updateActiveJobFolderContext={(item) => setJobFolderInfo(item)}
                userSlug={cvDetail?.slug}
                {...(cvDetail?.price !== undefined
                  ? { defaultPrice: { price: Number(cvDetail.price), fullPrice: Number(cvDetail.fullPrice ?? 0) } }
                  : {})}
                onSendSuccess={(val) => {
                  if (val) {
                    confirmUnlock();
                  } else {
                    cancelUnlock();
                    setIsValidateFlowLoading(false);
                  }
                }}
              />


            )}
            onClose={() => null}
          />
          <Modal
            isOpen={sendMessageModal}
            body={(
              <SendMessageModalContent
                imageUrl={cvDetail?.imgUrl ? cvDetail.imgUrl : ''}
                candidateName={cvDetail?.privateInformation?.name ? cvDetail.privateInformation.name : ''}
                onSendSuccess={() => setSendMessageModal(false)}
                onClose={() => {
                  setSendMessageModal(false);
                }}
                slug={slug}
                trackingId={rid}
                locale={locale}
              />
            )}
            onClose={() => {
              setSendMessageModal(false);
            }}
          />

          {/* Interview Questions Modal */}
          <InterviewQuestionsModal
            isOpen={isQuestionsModalOpen}
            onClose={handleCloseQuestionsModal}
            questions={generatedQuestions}
            onSave={handleSaveQuestions}
            isSaving={isSavingQuestions}
          />
        </>
      )}

      {/* Available operations */}
      {isAuth && availableOperations !== undefined && availableOperations?.length > 0
        && (
          <section
            className={`${isSinglePage ? 'mb-16 md:mb-4' : ' mb-4'} fixed bottom-0 left-1/2 z-[11] -translate-x-1/2 rounded-full bg-surface-100 p-2 shadow-[0_35px_60px_25px_rgba(0,0,0,0.3)]`}
          >
            <div className="flex items-center justify-center">

              {isLoadingStatusChange && (
                <div className="flex items-center">
                  <div className="inline-flex min-w-10
                                cursor-pointer items-center justify-center
                                rounded-full bg-slate-200 px-4.5
                                py-3 text-md font-medium transition-all duration-200
                                hover:shadow-md  disabled:opacity-60"
                  >
                    <TailwindSpinner className="!mx-0 size-5" />
                  </div>
                </div>
              )}

              {/* Left buttons for resume operations (rejected related) */}
              {availableOperationsButtons(true)}

              {/* Contact dropdown -  call, message, view */}
              <Dropdown>
                {/* button to view options */}
                <DropdownButton
                  as={Button}
                  color="secondary"
                  rounding="full"
                  className="relative mx-1 flex h-12 min-w-32 items-center justify-center !text-md text-white hover:!bg-secondary/90"
                >
                  <>
                    <span>
                      {t('cv.detail.action.contact.label')}
                    </span>

                    <div className="absolute -bottom-2 left-1/2 flex -translate-x-1/2 flex-col items-center">
                      {returnUserStatus()}
                    </div>
                  </>
                </DropdownButton>

                {/* Dropdown list */}
                <DropdownItems
                  animation="slideUp"
                  className="bottom-full mb-2 flex min-w-48 flex-col items-start gap-3 !rounded-md border-t-8 border-secondary p-4"
                >

                  {/* Direct call - only for online users */}
                  {(isFreeCvInfoUnblock || cvDetail?.locked) && (
                    <div
                      className="absolute -top-3.5 left-1/2 min-w-max -translate-x-1/2 whitespace-nowrap rounded-lg bg-secondary px-1 text-pico font-semibold text-surface"
                    >
                      {isFreeCvInfoUnblock && (
                        <span>{t('cv.detail.action.contact.free')}</span>
                      )}

                      {cvDetail?.locked === true && (
                        hasSourcingPackage ? (
                          <span>{t('sourcing.contact.badge')}</span>
                        ) : (
                          <>
                            {`${t('cv.detail.contact.label')}:`}
                            {isPromoPrice && (
                              <span className="ml-1 text-danger line-through">
                                {cvDetail?.fullPrice}
                              </span>
                            )}
                            <span className="ml-1">
                              {cvDetail?.price}
                              {' '}
                              {t('global.label.credits')}
                            </span>
                          </>
                        )
                      )}
                    </div>
                  )}


                  <DropdownItem
                    as={Button}
                    styling="text"
                    color="ink"
                    className="block md:hidden"
                    onClick={(event) => { void onViewContactDataPressed(event, true); }}
                  >
                    <PhoneIcon className="mr-2 size-4" />
                    <span className="">
                      {t('cv.detail.action.call_direct.label')}
                    </span>
                  </DropdownItem>

                  {isCompany && cvDetail?.id !== userId && (
                    <DropdownItem
                      as={Button}
                      styling="text"
                      color="ink"
                      onClick={() => checkUnlockPdf()}
                    >
                      {/* Download CV button */}
                      <>
                        <ArrowDownTrayIcon className="mr-2 size-5 text-ink" />
                        {t('global.action.download.label')}
                      </>
                    </DropdownItem>
                  )}

                  {/* Send message */}
                  <DropdownItem
                    as={Button}
                    styling="text"
                    color="ink"
                    onClick={() => unlockWithMessage()}
                  >
                    <ChatBubbleLeftEllipsisIcon className="mr-2 size-4" />
                    <span className="flex items-center">
                      {t('cv.detail.action.message.label')}
                      {cvDetail?.hasUnreadMessage && (
                        <span className="ml-2 block size-2.5 rounded-full bg-error" />
                      )}
                    </span>
                  </DropdownItem>

                  {/* View contact data */}
                  <DropdownItem
                    as={Button}
                    styling="text"
                    color="ink"
                    onClick={(event) => { void onViewContactDataPressed(event, true); }}
                  >
                    <div>
                      <AddressBookSolidIcon className="mr-2.5 h-4 w-3.5 fill-ink" />
                    </div>
                    <span className="">
                      {t('cv.detail.action.see_contact.label')}
                    </span>
                  </DropdownItem>
                </DropdownItems>
              </Dropdown>

              {/* Interview request button - conditional rendering based on eligibleForInterviewRequest and status */}
              {(() => {
                const eligibleForInterviewRequest = cvDetail?.application?.eligibleForInterviewRequest;
                const interviewRequestStatus = cvDetail?.application?.interviewRequest?.status;

                // Determine if we should show the "requested" state
                const showRequestedState = interviewRequestSent || (eligibleForInterviewRequest === false && interviewRequestStatus != null);

                // Hide button if eligibleForInterviewRequest is false and has no status
                const shouldHideButton = eligibleForInterviewRequest === false && interviewRequestStatus == null;

                if (shouldHideButton) {
                  return null;
                }

                // Determine button content based on state and device
                let buttonContent;
                if (showRequestedState && isMobile) {
                  // Show "requested" state with star icon (mobile)
                  buttonContent = <StarIcon className="size-5" />;
                } else if (showRequestedState) {
                  // Show "requested" state with star icon + text (desktop)
                  buttonContent = (
                    <>
                      <StarIcon className="mr-2 size-5" />
                      <span>{t('cv.detail.action.validate.requested')}</span>
                    </>
                  );
                } else if (isMobile) {
                  // Show normal state (mobile)
                  buttonContent = <CheckCircleIcon className="size-5" />;
                } else {
                  // Show normal state (desktop)
                  buttonContent = <span>{t('cv.detail.action.validate')}</span>;
                }

                // CV is locked if it's not the user's own CV and has no phone number or it's blurred
                const isCvLocked = isCompany && cvDetail?.id !== userId
                  && !cvDetail?.privateInformation?.phone
                  && !cvDetail?.privateInformation?.email;

                return (
                  <Button
                    rounding="full"
                    className={`relative mx-1 flex items-center justify-center px-4.5 py-3.5 !text-md hover:border-0 hover:!border-transparent disabled:bg-warning/20 disabled:!text-orange-600 disabled:opacity-60 md:h-12 md:min-w-32 ${
                      showRequestedState
                        ? 'bg-warning/20 !text-warning'
                        : 'bg-white !text-primary hover:bg-warning hover:!text-surface'
                    }`}
                    onClick={handleValidateClick}
                    disabled={(!eligibleForInterviewRequest && !isCvLocked)
                      || interviewRequestSent
                      || isValidateFlowLoading
                      || isInterviewRequestLoading
                      || isGeneratingQuestions
                      || cvDetail?.application?.interviewRequest !== undefined}
                    isLoading={isValidateFlowLoading || isInterviewRequestLoading || isGeneratingQuestions}
                  >
                    {buttonContent}
                  </Button>
                );
              })()}

              {/* Right buttons for resume operations (other than rejected) */}
              {availableOperationsButtons(false)}

            </div>
          </section>
        )}

      {/* Profile navigation and language selector - sticky only in modal */}
      <div
        className={`border-y bg-surface-100 py-2 transition-transform duration-300 ${
          closeCvModal ? 'sticky z-1 self-start' : ''
        } ${(withNavbarInModal || isSinglePage) ? ' sticky top-stickUnderHeader z-1' : ' sticky top-0 z-1 '}`}
      >
        <div className="container mx-auto">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-start gap-1.5 md:gap-4">

              { /* Close modal button */
                closeCvModal && (
                  <Tooltip tooltipText={t('global.btn.close')} position="bottom" colorScheme="default">
                    <Button styling="none" onClick={closeCvModal} aria-label={t('global.btn.close')}>
                      <span className="flex size-7 items-center justify-center rounded-full bg-surface md:size-8">
                        <XMarkIcon className="size-5 text-ink" />
                      </span>
                    </Button>
                  </Tooltip>
                )
              }

              {/* Share CV button */}
              <Tooltip tooltipText={t('cv.action.share')} position="bottom" colorScheme="default">
                <Button
                  styling="none"
                  aria-label={t('cv.action.share')}
                  onClick={toggleShareModal}
                  className="flex size-7 items-center justify-center rounded-full bg-surface md:size-8"
                >
                  <ShareIcon className="size-4 text-ink" />
                </Button>
              </Tooltip>

              {/* Download CV button */}
              {isAuth && isCompany && cvDetail?.id !== userId && (
                <Tooltip tooltipText={t('global.action.download.label')} position="bottom" colorScheme="default">
                  <Button
                    styling="none"
                    aria-label={t('global.action.download.label')}
                    onClick={() => checkUnlockPdf()}
                    className="flex size-7 items-center justify-center rounded-full bg-surface md:size-8"
                    spinnerClass="h-5 w-5"
                  >
                    <ArrowDownTrayIcon className="size-4 text-ink" />
                  </Button>
                </Tooltip>
              )}

              {/* Message unlock */}
              {isAuth && (
                <Tooltip tooltipText={t('cv.detail.action.message.label')} position="bottom" colorScheme="default">
                  <Button
                    styling="none"
                    aria-label={t('global.action.download.label')}
                    onClick={() => unlockWithMessage()}
                    className="flex size-7 items-center justify-center rounded-full bg-surface md:size-8"
                    spinnerClass="h-5 w-5"
                  >
                    <ChatBubbleLeftEllipsisIcon className="size-5 fill-ink" />
                  </Button>
                </Tooltip>
              )}

              {/* Unlock with contact data */}
              {isAuth && (
                <Tooltip tooltipText={t('cv.detail.action.see_contact.label')} position="bottom" colorScheme="default">
                  <Button
                    styling="none"
                    aria-label={t('cv.detail.action.see_contact.label')}
                    onClick={(event) => { void onViewContactDataPressed(event, true); }}
                    className="flex size-7 items-center justify-center rounded-full bg-surface md:size-8"
                    spinnerClass="h-5 w-5"
                  >
                    <AddressBookSolidIcon className="size-3 fill-ink" />
                  </Button>
                </Tooltip>
              )}
            </div>

            {/* This component return the arrows to the navigation between items. While we do not have the list in Next.js, we are storing in cookie */}
            {/* the value of the last search (filters), then in react we are getting a list of CV-s for that search. We are sending also the offset */}
            {/* and then manipulate each case in the component */}

            <ResumeArrowsNavigation
              setLoadingCvStatusFromOperation={setLoadingCvStatusFromOperation}
              jobSlug={jobSlug}
              isFromJobContext={isFromJobContext}
              slug={slug}
            />

          </div>
        </div>
      </div>
    </>
  );
};
